import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useCombinedRefs } from '../../util/useCombinedRefs.js';
import { useDebounce } from '../../util/useDebounce.js';
import { isTouchDevice } from '@drone-js/media-query';
import { Box } from '../Box/Box.js';
import { Navigator as NavigatorMemoized } from '../Navigator/Navigator.js';
import { useDirectionContext, Direction } from '../../contexts/session/Locale.js';
import { useMediaQueryContext } from '../../contexts/system/MediaQuery.js';

var _excluded = ["behavior", "previousLabel", "nextLabel", "children", "active", "onActiveChange", "focusableContainer", "restrictFocus"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var fadeWidth = 52;
var fadeStopsStart = [{
  backgroundColor: 'transparent'
}, {
  backgroundColor: 'opaque',
  position: "".concat(fadeWidth, "px")
}];
var fadeStopsEnd = [{
  backgroundColor: 'opaque',
  position: "calc(100% - ".concat(fadeWidth, "px)")
}, {
  backgroundColor: 'transparent'
}];
var fadeStops = function fadeStops(start, end) {
  if (start && end) return fadeStopsStart.concat(fadeStopsEnd);
  if (start) return fadeStopsStart;
  if (end) return fadeStopsEnd;
  return undefined;
};
var shouldShowFade = function shouldShowFade(context) {
  var isSmallBreakpoint = !context.m;
  var hideFade = isSmallBreakpoint;
  return !hideFade;
};
var maskGradient = function maskGradient(start, end, mediaQueryContext) {
  var enabled = shouldShowFade(mediaQueryContext);
  if (!enabled) return undefined;
  var stops = fadeStops(start, end);
  if (!stops) return undefined;
  return {
    angle: 90,
    stops: stops
  };
};
var resolveHasNext = function resolveHasNext(_ref) {
  var isRtl = _ref.isRtl,
    scrollLeft = _ref.scrollLeft,
    scrollRef = _ref.scrollRef,
    lastItemRect = _ref.lastItemRect,
    scrollRect = _ref.scrollRect;
  if (isRtl) return Math.abs(scrollLeft) < scrollRef.scrollWidth - scrollRef.clientWidth;
  if (lastItemRect) return Math.floor(lastItemRect.right) > Math.floor(scrollRect.right);
  return false;
};
var SCROLL_DEBOUNCE_TIMEOUT_MS = 150;

/**
 * HorizontalScroll is a container component that allows horizontal scrolling of its children. For accessibility, it includes the navigation buttons to scroll forward and backward.
 */
var HorizontalScroll = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var behavior = props.behavior,
    previousLabel = props.previousLabel,
    nextLabel = props.nextLabel,
    children = props.children,
    activeProp = props.active,
    onActiveChange = props.onActiveChange,
    focusableContainer = props.focusableContainer,
    restrictFocus = props.restrictFocus,
    rest = _objectWithoutProperties(props, _excluded);
  var direction = useDirectionContext();
  var isRtl = direction === Direction.RTL;
  var mediaQueryContext = useMediaQueryContext();
  var childrenRef = React.useRef([]);
  var containerRef = React.useRef(null);
  var containerCombinedRef = useCombinedRefs(ref, containerRef);
  var _React$useState = React.useState(activeProp || 0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    active = _React$useState2[0],
    setActive = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    hasPreviousItem = _React$useState4[0],
    setHasPreviousItem = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    hasNextItem = _React$useState6[0],
    setHasNextItem = _React$useState6[1];
  var _React$useState7 = React.useState([]),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    visibleCards = _React$useState8[0],
    setVisibleCards = _React$useState8[1];
  var shouldShowButtons = !isTouchDevice();
  var shouldInertInvisibleCards = shouldShowButtons && restrictFocus; // Hides and inerts cards not currently in view port
  var childrenArray = React.Children.toArray(children);
  var childrenProp = React.Children.map(children, function (child, i) {
    if (! /*#__PURE__*/React.isValidElement(child)) return null;
    var attributes = shouldInertInvisibleCards && {
      'aria-hidden': !visibleCards[i] ? 'true' : undefined,
      inert: !visibleCards[i] ? '' : undefined
    };
    var style = _objectSpread(_objectSpread({}, child.props.style), {}, {
      scrollSnapAlign: props.scrollSnapType === 'mandatory' ? 'start' : undefined
    });
    var item = /*#__PURE__*/React.cloneElement(child, _objectSpread({
      ref: function ref(el) {
        if (el) childrenRef.current[i] = el;
      },
      style: style
    }, attributes));
    return item;
  });
  var updateButtonVisibility = React.useCallback(function () {
    var _childrenRef$current;
    var scrollRef = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
    if (!scrollRef) return;
    var scrollRect = scrollRef.getBoundingClientRect();
    var lastItemRect = (_childrenRef$current = childrenRef.current[childrenRef.current.length - 1]) === null || _childrenRef$current === void 0 ? void 0 : _childrenRef$current.getBoundingClientRect();
    var scrollLeft = Math.floor(scrollRef.scrollLeft);
    var hasPrevious = Math.abs(scrollLeft) > 0;
    var hasNext = resolveHasNext({
      isRtl: isRtl,
      scrollLeft: scrollLeft,
      scrollRef: scrollRef,
      lastItemRect: lastItemRect,
      scrollRect: scrollRect
    });
    setHasPreviousItem(hasPrevious);
    setHasNextItem(hasNext);
  }, [containerRef, isRtl]);
  React.useLayoutEffect(function () {
    updateButtonVisibility();
  }, [updateButtonVisibility, childrenArray]);
  var handleScroll = useDebounce(updateButtonVisibility, SCROLL_DEBOUNCE_TIMEOUT_MS);
  React.useEffect(function () {
    var scrollRef = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
    scrollRef === null || scrollRef === void 0 || scrollRef.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return function () {
      return scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, handleScroll, updateButtonVisibility]);
  var scrollByPage = React.useCallback(function (type) {
    var scrollRef = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
    if (!scrollRef) return;
    var scrollWidth = isRtl ? -scrollRef.offsetWidth : scrollRef.offsetWidth;
    var fadeOffset = isRtl ? -fadeWidth : fadeWidth;
    var scrollTarget = type === 'next' ? scrollRef.scrollLeft + scrollWidth - fadeOffset : scrollRef.scrollLeft - scrollWidth + fadeOffset;
    scrollRef.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    });
  }, [containerRef, isRtl]);
  var getItemCenterPosition = React.useCallback(function (item, container) {
    var itemRect = item.getBoundingClientRect();
    var containerRect = container.getBoundingClientRect();
    var scrollLeft = container.scrollLeft,
      containerWidth = container.clientWidth;
    var halfContainerWidth = containerWidth / 2;
    var halfItemWidth = itemRect.width / 2;
    var rectStart = function rectStart(rect) {
      return isRtl ? rect.right : rect.left;
    };
    var directionMultiplier = isRtl ? -1 : 1;
    var itemStartOffset = rectStart(itemRect) - rectStart(containerRect) + scrollLeft;
    var centerCompensation = (halfContainerWidth - halfItemWidth) * directionMultiplier;
    var inlineCenter = itemStartOffset - centerCompensation;
    return inlineCenter;
  }, [isRtl]);
  var scrollItemToCenter = React.useCallback(function (item, container) {
    if (!item || !container) return;
    var inlineCenter = getItemCenterPosition(item, container);
    container.scrollTo({
      left: inlineCenter,
      behavior: 'smooth'
    });
  }, [getItemCenterPosition]);
  var scrollToIndex = React.useCallback(function (index) {
    var target = childrenRef.current[index];
    scrollItemToCenter(target, containerRef.current);
  }, [containerRef, scrollItemToCenter]);
  var handleActiveChange = React.useCallback(function (newActive) {
    onActiveChange === null || onActiveChange === void 0 || onActiveChange(newActive);
    if (activeProp === undefined) {
      setActive(newActive);
      scrollToIndex(newActive);
    }
  }, [activeProp, onActiveChange, scrollToIndex]);
  React.useEffect(function () {
    if (behavior === 'item' && activeProp !== undefined && activeProp !== active) {
      setActive(activeProp);
      scrollToIndex(activeProp);
    }
  }, [activeProp, active, behavior, scrollToIndex, handleActiveChange]);
  var onPreviousClick = React.useCallback(function () {
    if (behavior === 'page') {
      scrollByPage('previous');
      return;
    }
    var targetIndex = Math.max(active - 1, 0);
    handleActiveChange(targetIndex);
  }, [active, behavior, handleActiveChange, scrollByPage]);
  var onNextClick = React.useCallback(function () {
    if (behavior === 'page') {
      scrollByPage('next');
      return;
    }
    var targetIndex = Math.min(active + 1, childrenArray.length - 1);
    handleActiveChange(targetIndex);
  }, [behavior, active, childrenArray.length, handleActiveChange, scrollByPage]);
  React.useEffect(function () {
    if (shouldInertInvisibleCards) {
      var handleIntersectionObserver = function handleIntersectionObserver(entries) {
        var cardsInView = [];
        entries.forEach(function (entry, entryIndex) {
          cardsInView[entryIndex] = entry.isIntersecting;
        });
        setVisibleCards(cardsInView);
      };
      // eslint-disable-next-line compat/compat
      var observer = new IntersectionObserver(handleIntersectionObserver, {
        root: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current,
        rootMargin: '0px',
        threshold: 0.5 // 50% of the card should be visible
      });
      if (containerRef !== null && containerRef !== void 0 && containerRef.current) {
        Array.from(containerRef === null || containerRef === void 0 ? void 0 : containerRef.current.children).forEach(function (item) {
          observer.observe(item);
        });
      }
      return function () {
        return observer.disconnect();
      };
    }
    return undefined;
  }, [containerRef, childrenArray, shouldInertInvisibleCards]);
  var previousButton = shouldShowButtons && /*#__PURE__*/React.createElement(NavigatorMemoized, {
    acceptPointer: false,
    variant: "inside",
    align: "start",
    size: "s",
    mx: "8",
    width: "".concat(fadeWidth, "px"),
    disabled: false,
    visible: hasPreviousItem,
    accessibilityLabel: previousLabel,
    onClick: onPreviousClick,
    zIndex: 1
  });
  var nextButton = shouldShowButtons && /*#__PURE__*/React.createElement(NavigatorMemoized, {
    acceptPointer: false,
    variant: "inside",
    align: "end",
    size: "s",
    mx: "8",
    width: "".concat(fadeWidth, "px"),
    disabled: false,
    visible: hasNextItem,
    accessibilityLabel: nextLabel,
    onClick: onNextClick,
    zIndex: 1
  });
  var scrollableContainer = /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
    ref: containerCombinedRef,
    hideScrollbar: true,
    display: "flex",
    overflowX: "auto",
    tabIndex: focusableContainer ? 0 : undefined,
    width: "100%",
    maskImage: maskGradient(hasPreviousItem, hasNextItem, mediaQueryContext)
  }), childrenProp);
  return /*#__PURE__*/React.createElement(Box, {
    maxWidth: "100%",
    position: "relative",
    overflow: "hidden"
  }, previousButton, scrollableContainer, nextButton);
});
HorizontalScroll.defaultProps = {
  behavior: 'page',
  restrictFocus: true
};
var HorizontalScrollMemoized = /*#__PURE__*/React.memo(HorizontalScroll);
HorizontalScrollMemoized.displayName = 'HorizontalScroll';

export { HorizontalScrollMemoized as HorizontalScroll };
//# sourceMappingURL=HorizontalScroll.js.map
